// Wrapper fino de `fetch` (`spec/17-frontend.md` §4.2 e §4.3). Único lugar que fala
// com a API: cookie do Better Auth direto, sem BFF.

import { API_BASE_URL } from '../app/config.ts';
import { type Mode, readStoredMode } from '../app/mode.tsx';
import { ApiError, toApiError } from './errors.ts';

const SELLER_PREFIX = '/v1/me';

/**
 * Step-up (`spec/17` §4.3): a ação sensível é executada; se voltar `403 mfa_required`,
 * o `StepUpDialog` pede o código e **a chamada original é reexecutada aqui**. Ficar
 * neste arquivo é o que garante que nenhuma tela reimplemente o fluxo.
 *
 * O handler devolve `false` quando não há step-up possível (2FA desabilitada, ou o
 * provider ainda não montou): aí o `ApiError` sobe e o roteador de erro decide.
 */
type StepUpHandler = () => Promise<boolean>;

let stepUpHandler: StepUpHandler | undefined;

export function setStepUpHandler(handler: StepUpHandler | undefined): void {
  stepUpHandler = handler;
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  /** Serializado como JSON. `undefined` não manda corpo nem `Content-Type`. */
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined>;
  signal?: AbortSignal;
  /** Override do modo; o default é o toggle global (só rotas de seller). */
  mode?: Mode;
  /** Idempotency-Key de `spec/00` §4.7, quando a rota exigir. */
  idempotencyKey?: string;
  /**
   * Token do Cloudflare Turnstile, no header `x-captcha-response` (SEC-01,
   * `spec/03` §8). Opção POR CHAMADA e nunca header global: só os três endpoints
   * públicos de auth pedem captcha, e o token é de uso único.
   */
  captchaToken?: string;
}

function buildUrl(path: string, query: RequestOptions['query']): string {
  const url = new URL(`${API_BASE_URL}${path}`);
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value !== undefined) {
      url.searchParams.set(key, String(value));
    }
  }
  return url.toString();
}

function buildInit(path: string, options: RequestOptions): RequestInit {
  const headers = new Headers({
    Accept: 'application/json',
    // CSRF em profundidade (`spec/03` §8). Mandado sempre, inclusive no admin.
    'X-Requested-With': 'lm',
  });
  if (path.startsWith(SELLER_PREFIX)) {
    headers.set('X-Mode', options.mode ?? readStoredMode());
  }
  if (options.idempotencyKey !== undefined) {
    headers.set('Idempotency-Key', options.idempotencyKey);
  }
  if (options.captchaToken !== undefined) {
    headers.set('x-captcha-response', options.captchaToken);
  }
  if (options.body !== undefined) {
    headers.set('Content-Type', 'application/json');
  }
  return {
    method: options.method ?? 'GET',
    headers,
    credentials: 'include',
    ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
    ...(options.signal === undefined ? {} : { signal: options.signal }),
  };
}

async function readBody(response: Response): Promise<unknown> {
  const text = await response.text();
  if (text === '') {
    return undefined;
  }
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

async function send(path: string, options: RequestOptions): Promise<Response> {
  return fetch(buildUrl(path, options.query), buildInit(path, options));
}

/**
 * `T` vem sempre de `src/api/types/{public,admin}.d.ts`, nenhuma tela escreve tipo de
 * resposta à mão (`spec/17` §5.2).
 */
export async function api<T>(path: string, options: RequestOptions = {}): Promise<T> {
  let response = await send(path, options);

  if (!response.ok) {
    const error = toApiError(response, await readBody(response));
    if (error.code === 'mfa_required' && stepUpHandler !== undefined) {
      const verified = await stepUpHandler();
      if (verified) {
        response = await send(path, options);
        if (!response.ok) {
          throw toApiError(response, await readBody(response));
        }
        return (await readBody(response)) as T;
      }
    }
    throw error;
  }

  return (await readBody(response)) as T;
}

export { ApiError };
